import { Injectable } from '@angular/core';
import { SvgEditExtension, SvgEditExtensionObject } from '@models/svg-edit-extension';
import { CanvasService } from './canvas.service';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { SelectedElementsService } from './selected-elements.service';

@Injectable({ providedIn: 'root' })
export class InputValueExtensionService implements SvgEditExtension {
    name: string = 'inputValueExtension';

    constructor(private plugin: InputValueExtensionPlugin) {
        this.init = this.init.bind(this);
    }

    init(): SvgEditExtensionObject {
        this.bindMouseEvents();
        return this.plugin;
    }

    udpateInputUnit(element, channelUnits) {
        this.plugin.udpateInputUnit(element, channelUnits);
    }

    addEventListeners(element) {
        this.plugin.addEventListeners(element);
    }

    private bindMouseEvents() {
        return this.plugin.bindMouseEvents();
    }
}

@Injectable({ providedIn: 'root' })
class InputValueExtensionPlugin implements SvgEditExtensionObject {
    constructor(
        private canvasService: CanvasService,
        private inputMode: CanvasInputModeService,
        private selectedElementsService: SelectedElementsService
    ) {}

    private unitsEventName = 'inputValueUnitsUpdated';

    callback(deps: any) {}

    bindMouseEvents() {
        document.addEventListener('mousedown', this.handleMouseDown.bind(this));
        document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        document.addEventListener('mouseup', this.handleMouseUp.bind(this));
    }

    private handleMouseDown(event: MouseEvent) {
        const svgRoot = this.canvasService.getSvgRoot() as unknown as Element;
        if (!svgRoot || typeof svgRoot !== 'object') return;
        const layer = (svgRoot as Element).querySelector?.('.layer') as Element;
        if (!layer) return;
        const inputs = layer.querySelectorAll(`foreignObject[type="${CanvasInputMode.INPUT_VALUE}"] input`);
        if (inputs.length > 0) {
            Array.from(inputs).forEach((item: Element) => {
                (item as HTMLInputElement).blur();
                (item as HTMLInputElement).style.color = '#ffffff';
            });
        }
    }

    private handleMouseMove(event: MouseEvent) {}

    private static readonly BUTTON_W = 56;
    private static readonly GAP = 8;
    private static readonly INPUT_W = 90;
    private static readonly DESIGN_WIDTH = 56 + 8 + 90;
    private static readonly INITIAL_WIDTH = 198;
    private static readonly HEIGHT = 24;
    private static readonly BORDER = '1px solid #cfd8dc';
    private static readonly FONT_SIZE = 16;

    private handleMouseUp(event: MouseEvent) {
        if (this.inputMode.getMode() === CanvasInputMode.INPUT_VALUE) {
            const svgRoot = this.canvasService.getSvgRoot() as unknown as Element;
            if (!svgRoot || typeof svgRoot !== 'object') return;
            if (event.target !== svgRoot && !(svgRoot as Element).contains(event.target as Node)) {
                return;
            }
            const canvas = this.canvasService.getCanvas();

            const rootSctm = this.canvasService.getSCTM();
            const pt = this.canvasService.transformPoint(event.clientX, event.clientY, rootSctm);
            const mouseX = pt.x;
            const mouseY = pt.y;

            const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
            foreignObject.id = canvas.getNextId();
            foreignObject.setAttribute('x', mouseX.toString());
            foreignObject.setAttribute('y', mouseY.toString());
            foreignObject.setAttribute('type', CanvasInputMode.INPUT_VALUE);
            foreignObject.setAttribute('width', InputValueExtensionPlugin.INITIAL_WIDTH.toString());
            foreignObject.setAttribute('height', InputValueExtensionPlugin.HEIGHT.toString());
            foreignObject.setAttribute('pointer-events', 'all');

            const container = document.createElement('div');
            container.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            container.setAttribute('style', 'display: flex; align-items: center; gap: 8px');
            container.style.width = '100%';
            container.style.height = '100%';

            const inputContainer = document.createElement('div');
            inputContainer.setAttribute('inputContainer', 'true');
            inputContainer.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            inputContainer.style.display = 'flex';
            inputContainer.style.alignItems = 'center';
            inputContainer.style.flex = '0 0 auto';
            inputContainer.style.boxSizing = 'border-box';
            inputContainer.style.overflow = 'hidden';
            inputContainer.style.minWidth = '0';
            inputContainer.style.width = InputValueExtensionPlugin.INPUT_W + 'px';
            inputContainer.style.height = InputValueExtensionPlugin.HEIGHT + 'px';
            inputContainer.style.backgroundColor = '#161a1e';
            inputContainer.style.border = InputValueExtensionPlugin.BORDER;
            inputContainer.style.borderRadius = '2px';
            inputContainer.style.color = '#32404f';

            const input = document.createElement('input');
            input.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            input.style.flex = '1 1 0';
            input.style.minWidth = '0';
            input.style.width = '100%';
            input.style.boxSizing = 'border-box';
            input.style.background = 'transparent';
            input.style.border = 'none';
            input.style.outline = 'none';
            input.style.margin = '0 0 0 2px';
            input.style.padding = '4px';
            input.style.userSelect = 'text';
            input.setAttribute('value', '*********');
            input.value = '*********';
            input.style.textAlign = 'left';
            input.style.fontFamily = 'NotoSans';
            input.style.fontSize = InputValueExtensionPlugin.FONT_SIZE + 'px';
            input.style.fontWeight = 'normal';
            input.style.fontStyle = 'normal';
            input.style.fontStretch = 'normal';
            input.style.lineHeight = '1.33';
            input.style.letterSpacing = 'normal';
            input.style.color = '#ffffff';
            input.value = '*********';

            const unitsDivision = document.createElement('div');
            unitsDivision.setAttribute('unitsDivision', 'true');
            unitsDivision.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            unitsDivision.innerText = 'Unit';
            unitsDivision.style.flex = '0 0 auto';
            unitsDivision.style.whiteSpace = 'nowrap';
            unitsDivision.style.padding = '4px 4px 4px 0';
            unitsDivision.style.display = 'flex';
            unitsDivision.style.alignItems = 'center';
            unitsDivision.style.fontFamily = 'NotoSans';
            unitsDivision.style.fontSize = InputValueExtensionPlugin.FONT_SIZE + 'px';
            unitsDivision.style.fontWeight = 'normal';
            unitsDivision.style.fontStyle = 'normal';
            unitsDivision.style.fontStretch = 'normal';
            unitsDivision.style.lineHeight = '1.33';
            unitsDivision.style.letterSpacing = 'normal';
            unitsDivision.style.textAlign = 'left';
            unitsDivision.style.color = '#cfd8dc';

            inputContainer.appendChild(input);

            container.appendChild(inputContainer);
            container.appendChild(unitsDivision);

            foreignObject.appendChild(container);

            const layer = (svgRoot as Element).querySelector('.layer') as Element;
            if (layer) {
                layer.appendChild(foreignObject);
            }

            this.addEventListeners(foreignObject);
            canvas.addCommandToHistory(new canvas.history.InsertElementCommand(foreignObject));
            this.selectedElementsService.setSelectedElements([foreignObject]);
            this.inputMode.setMode(CanvasInputMode.SELECT);
        }
    }

    udpateInputUnit(foreignObject, units) {
        const unitsDivision = foreignObject.querySelector('[unitsDivision="true"]') as HTMLElement;
        unitsDivision.innerText = units;
    }

    applyLayout(foreignObject: Element) {
        const container = foreignObject.firstElementChild as HTMLElement;
        const inputContainer = foreignObject.querySelector('[inputContainer="true"]') as HTMLElement;
        const unitsDivision = foreignObject.querySelector('[unitsDivision="true"]') as HTMLElement;
        if (!container || !inputContainer || !unitsDivision) return;

        const width = parseFloat(foreignObject.getAttribute('width') ?? '0');
        const height = parseFloat(foreignObject.getAttribute('height') ?? '0');

        const scale = width / InputValueExtensionPlugin.DESIGN_WIDTH;
        const textScale = Math.min(scale, height / InputValueExtensionPlugin.HEIGHT);
        const fontSize = `${InputValueExtensionPlugin.FONT_SIZE * textScale}px`;

        container.style.gap = `${InputValueExtensionPlugin.GAP * scale}px`;

        inputContainer.style.width = `${InputValueExtensionPlugin.INPUT_W * scale}px`;
        inputContainer.style.height = `${height}px`;
        inputContainer.style.borderRadius = `${2 * textScale}px`;

        const input = inputContainer.querySelector('input') as HTMLInputElement;
        if (input) {
            input.style.fontSize = fontSize;
            input.style.margin = `0 0 0 ${2 * textScale}px`;
            input.style.padding = `${4 * textScale}px`;
        }

        unitsDivision.style.fontSize = fontSize;
        this.styleUnits(unitsDivision, scale, height);
    }

    private styleUnits(unitsDivision: HTMLElement, scale: number, height: number) {
        unitsDivision.style.flex = '0 0 auto';
        unitsDivision.style.whiteSpace = 'nowrap';
        unitsDivision.style.display = 'flex';
        unitsDivision.style.alignItems = 'center';
        unitsDivision.style.padding = '0';
        unitsDivision.style.width = `${InputValueExtensionPlugin.BUTTON_W * scale}px`;
        unitsDivision.style.height = `${height}px`;
        unitsDivision.style.overflow = 'hidden';
        unitsDivision.style.textOverflow = 'ellipsis';
    }

    addEventListeners(foreignObject) {
        const unitsDivision = foreignObject.querySelectorAll('[unitsDivision="true"]')[0];
        const buttonInput = foreignObject.querySelectorAll('[buttonInput="true"]')[0];
        const inputContainer = foreignObject.querySelectorAll('[inputContainer="true"]')[0];

        if (buttonInput) {
            buttonInput.remove();
            foreignObject.firstElementChild.appendChild(unitsDivision);
        }
        this.applyLayout(foreignObject);

        const isInput = (target: EventTarget | null): target is HTMLInputElement =>
            !!target && (target as Element).localName === 'input';

        const getInput = () => inputContainer.querySelector('input') as HTMLInputElement;

        inputContainer.addEventListener('focusin', (e: Event) => {
            if (!isInput(e.target)) return;
            e.target.style.color = '#ffffff';
            inputContainer.style.border = '1px solid #9cb2fb';
        });

        inputContainer.addEventListener('focusout', (e: Event) => {
            if (!isInput(e.target)) return;
            e.target.style.color = '#ffffff';
            inputContainer.style.border = InputValueExtensionPlugin.BORDER;
        });

        inputContainer.addEventListener('input', (e: Event) => {
            if (!isInput(e.target)) return;
            e.target.setAttribute('value', e.target.value);
        });

        inputContainer.addEventListener('click', () => {
            const input = getInput();
            if (document.activeElement !== input) {
                input.focus();
                input.select();
            }
        });

        ['keydown', 'keyup', 'mousedown'].forEach(name => {
            inputContainer.addEventListener(name, (e: Event) => e.stopPropagation());
        });

        const onOutsidePointerDown = (event: Event) => {
            if (!foreignObject.isConnected) return;
            if (inputContainer.contains(event.target as Node)) return;
            const input = getInput();
            if (document.activeElement === input) {
                input.blur();
            }
        };

        document.addEventListener('mousedown', onOutsidePointerDown, true);

        const initialInput = getInput();
        initialInput.style.color = '#ffffff';
        inputContainer.style.border = InputValueExtensionPlugin.BORDER;

        // buttonInput.addEventListener('click', () => {
        //     console.log('Send value', getInput().value);
        // });

        foreignObject.addEventListener(this.unitsEventName, (event: any) => {
            unitsDivision.innerHTML = event.detail;
        });

        const observer = new MutationObserver(() => this.applyLayout(foreignObject));

        observer.observe(foreignObject, {
            attributes: true,
            attributeFilter: ['width', 'height'],
        });
    }
}
